/**
 * RBAC scope — who the current user is in the role/permission UI and what
 * they may grant. Mirrors the backend ``GET /accounts/rbac-scope/``:
 *  - platform super-admins manage everything (global roles included);
 *  - company admins manage only their own company's roles, limited to
 *    resources within their entitlement envelope (``allowed_resources``).
 */
import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { isAuthenticated } from "@/lib/auth";

export interface RbacScope {
  is_super_admin: boolean;
  is_company_admin: boolean;
  company_id: string | null;
  allowed_resources: string[];
}

export function useRbacScope() {
  return useQuery<RbacScope>({
    queryKey: ["rbac-scope"],
    queryFn: () => api.get<RbacScope>("/accounts/rbac-scope/"),
    enabled: isAuthenticated(),
    staleTime: 5 * 60 * 1000,
  });
}

/** True for anyone allowed to open the Roles & permissions screen. */
export function canManageRbac(scope: RbacScope | undefined): boolean {
  return Boolean(scope?.is_super_admin || scope?.is_company_admin);
}
